import { useDistanceUnit } from "@/components/common/settings-context/settings-context";
import { DistanceInputField } from "@/components/ui/distance-input-field";
import { selectCadLevelsInWorldCs } from "@/store/cad/cad-slice";
import {
  selectCloudAndCadAlignReference,
  selectCloudCadAlignmentModelElevation,
} from "@/store/modes/cloud-cad-alignment-mode-selectors";
import {
  AlignmentReference,
  setModelElevationForCloudCadAlignment,
} from "@/store/modes/cloud-cad-alignment-mode-slice";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { Dropdown, grey } from "@faro-lotv/flat-ui";
import { Stack } from "@mui/material";
import { useCallback, useEffect, useRef, useState, type JSX } from "react";

/**
 * @returns cloud-cad alignment model elevation control panel
 */
export function ModelElevationTool(): JSX.Element {
  const dispatch = useAppDispatch();

  const modelElevation = useAppSelector(selectCloudCadAlignmentModelElevation);

  const reference = useAppSelector(selectCloudAndCadAlignReference);

  const cadLevelsDescription = useAppSelector(selectCadLevelsInWorldCs);
  const cadLevelsOptions = cadLevelsDescription?.map((level) => ({
    key: level.Name,
    value: level.Elevation.toString(),
    label: level.Name,
  }));

  // Define the state variables for the levels dropdown
  const [levelsDropdownSelection, setLevelsDropdownSelection] = useState("");

  // variable to keep track of whether the level dropdowns has been changed by the user
  const levelsDropdownChanged = useRef(false);

  // Update the UI when the elevation changes
  useEffect(() => {
    if (!levelsDropdownChanged.current) {
      setLevelsDropdownSelection("");
    }

    levelsDropdownChanged.current = false;
  }, [modelElevation]);

  const onSelectChanged = useCallback(
    (event: { target: { value: string } }) => {
      levelsDropdownChanged.current = true;
      const newHeight = parseFloat(event.target.value);
      setLevelsDropdownSelection(event.target.value);
      dispatch(setModelElevationForCloudCadAlignment(newHeight));
    },
    [dispatch],
  );

  const distanceUnit = useDistanceUnit();

  return (
    <Stack
      direction="column"
      spacing={2}
      sx={{
        borderRadius: "5px",
        borderTop: "1px",
        borderBottom: "1px",
        borderLeft: "1px",
        borderLeftColor: grey[1000],
        borderLeftStyle: "solid",
        borderRight: "1px",
        borderRightColor: grey[1000],
        borderRightStyle: "solid",
        px: 1,
        pt: 1,
        backgroundColor: grey[1000],
        position: "absolute",
        top: 80,
        height: "auto",
        right: reference === AlignmentReference.bimModel ? "1%" : "51%",
        width: "auto",
      }}
    >
      <Dropdown
        label="Level"
        options={
          cadLevelsOptions ?? [{ key: "N/A", value: "N/A", label: "N/A" }]
        }
        disabled={!cadLevelsOptions}
        sx={{
          height: "30px",
        }}
        dark
        value={cadLevelsOptions?.length ? levelsDropdownSelection : "N/A"}
        onChange={onSelectChanged}
      />

      <DistanceInputField
        fullWidth
        label="Elevation"
        distanceUnit={distanceUnit}
        dark
        sx={{ marginBottom: "8px", height: "30px" }}
        distanceInMeter={modelElevation}
        onDistanceChanged={(newValue) => {
          if (newValue !== undefined) {
            dispatch(setModelElevationForCloudCadAlignment(newValue));
          }
        }}
        errorMessage="Elevation must be a number"
      />
    </Stack>
  );
}
